'use client';

import { useQuery } from '@tanstack/react-query';
import { timeAgo } from '@/lib/terminal-format';
import { ExternalLink } from 'lucide-react';

interface NewsFeedProps {
  symbol: string;
  limit?: number;
}

export function NewsFeed({ symbol, limit = 8 }: NewsFeedProps) {
  const { data: news, isLoading } = useQuery<{ title: string; link: string; source: string; publishedAt: string }[]>({
    queryKey: ['/api/news', symbol],
    queryFn: async () => {
      const res = await fetch(`/api/news/${encodeURIComponent(symbol)}?count=${limit}`);
      if (!res.ok) return [];
      const data = await res.json();
      // Adapt equity-labs news API response shape: { articles: [...] }
      const articles = data.articles || [];
      return articles.map((a: { title: string; link: string; source: string; publishedAt: string }) => ({
        title: a.title,
        link: a.link,
        publisher: a.source,
        publishedAt: a.publishedAt,
      }));
    },
    refetchInterval: 300000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="p-2 border-b border-[#1F2937]">
            <div className="h-3 w-full mb-1 bg-[#1F2937] animate-pulse rounded" />
            <div className="h-2 w-32 bg-[#1F2937] animate-pulse rounded" />
          </div>
        ))}
      </div>
    );
  }

  const items = (news || []).slice(0, limit);

  if (items.length === 0) {
    return (
      <div className="p-4 text-center text-[#6B7280] text-xs">
        No recent news
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#1F2937]">
      {items.map((item: { title: string; link: string; publisher?: string; publishedAt?: string }, i: number) => (
        <a
          key={i}
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block p-2 hover:bg-[#1A2035] transition-colors group"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#E5E7EB] leading-tight line-clamp-2 group-hover:text-[var(--color-accent)] transition-colors">
                {item.title}
              </div>
              <div className="flex items-center gap-2 mt-1">
                {item.publisher && <span className="text-[10px] text-[#6B7280]">{item.publisher}</span>}
                {item.publishedAt && (
                  <span className="text-[10px] text-[#6B7280]">{timeAgo(item.publishedAt)}</span>
                )}
              </div>
            </div>
            <ExternalLink className="w-3 h-3 text-[#6B7280] opacity-0 group-hover:opacity-100 mt-0.5 flex-shrink-0" />
          </div>
        </a>
      ))}
    </div>
  );
}
